import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStringsContext } from '../../../../contexts/strings';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import PollAlternatioveItem, { PollAlternativeItemProps } from './poll-alternative-item';
import { InputWithValidateProps } from '../../../common/input/text-input-with-validate';
import { TextAlternative, TextPoll } from '../types';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

const getRandomId = () => Math.floor(Math.random() * (100000 - 10000) + 10000);

const defaultQuizAlternative: TextAlternative = {
  id: -1,
  text: '',
};

interface SortableItemProps extends PollAlternativeItemProps {
  id: number;
}

const SortableItem = ({ id, ...props }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box sx={style} ref={setNodeRef} {...attributes}>
      <PollAlternatioveItem isActive={isDragging} listeners={listeners} {...props} />
    </Box>
  );
};

type AlternativesTextKey = keyof Pick<TextAlternative, 'text'>;
type TextAlternativesKey = keyof Pick<TextPoll, 'alternatives'>;

const textKey: AlternativesTextKey = 'text';
const alternativesKey: TextAlternativesKey = 'alternatives';

interface Props extends Omit<InputWithValidateProps, 'onValue'> {
  onValue: (key: TextAlternativesKey, value: any | null) => void;
  alternatives?: TextAlternative[];
  isEditing: boolean;
}

const PollTextAlternatives = ({ isEditing, alternatives, onValue, hideValidationError }: Props) => {
  const { strings } = useStringsContext();
  const sensors = useSensors(useSensor(PointerSensor));
  const initAlternativesValues = useRef(false);

  const [textAlternatives, setTextAlternatives] = useState<TextAlternative[]>(
    isEditing && alternatives
      ? [...alternatives]
      : [
          { ...defaultQuizAlternative, id: getRandomId() },
          { ...defaultQuizAlternative, id: getRandomId() },
        ]
  );
  const [alternativeActive, setAlternativeActive] = useState<TextAlternative | null>(null);

  useEffect(() => {
    if (!initAlternativesValues.current) {
      onValue(alternativesKey, textAlternatives);
      initAlternativesValues.current = true;
    }
  }, [onValue, textAlternatives]);

  const handleDragStart = (event: any) => {
    const { active } = event;
    const alternative = textAlternatives.find(({ id }) => id === active.id);
    setAlternativeActive(alternative!);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = textAlternatives.findIndex(({ id }) => id === active.id);
      const newIndex = textAlternatives.findIndex(({ id }) => id === over.id);
      const updatedAlternatives = arrayMove(textAlternatives, oldIndex, newIndex);
      setTextAlternatives(updatedAlternatives);
      onValue(alternativesKey, updatedAlternatives);
    }
    setAlternativeActive(null);
  };

  const handleAlternativesValue = useCallback(
    (
      key: AlternativesTextKey,
      value: string | null,
      alternative: TextAlternative,
      alternativeIndex: number
    ) => {
      alternative[key] = value || '';
      const updatedAlternatives = textAlternatives.map((a, index) =>
        index === alternativeIndex ? alternative : a
      );
      setTextAlternatives(updatedAlternatives);
      onValue(alternativesKey, updatedAlternatives);
    },
    [onValue, textAlternatives]
  );

  const onAddAlternative = useCallback(() => {
    const updatedAlternatives = [
      ...textAlternatives,
      { ...defaultQuizAlternative, id: getRandomId() },
    ];
    setTextAlternatives(updatedAlternatives);
    onValue(alternativesKey, updatedAlternatives);
  }, [onValue, textAlternatives]);

  const onDelete = useCallback(
    (alternativeIndex: number) => {
      textAlternatives.splice(alternativeIndex, 1);
      setTextAlternatives([...textAlternatives]);
      onValue(alternativesKey, textAlternatives);
    },
    [onValue, textAlternatives]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <Stack spacing={2}>
        <Typography variant="body2-bold">{strings.polls.quizAlternativeTextTitle}</Typography>
        <Stack spacing={4}>
          <SortableContext items={textAlternatives} strategy={verticalListSortingStrategy}>
            {textAlternatives.map((alternative, index) => (
              <SortableItem
                key={alternative.id}
                id={alternative.id}
                label={strings.polls.quizAlternativeLabel + String(index + 1)}
                name={textKey}
                value={alternative.text}
                onValue={(key, value) =>
                  handleAlternativesValue(textKey, value, alternative, index)
                }
                onDelete={() => onDelete(index)}
                disabled={textAlternatives.length < 3}
                hideValidationError={hideValidationError}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {alternativeActive ? (
              <PollAlternatioveItem
                name={textKey}
                value={alternativeActive.text}
                label=""
                hideValidationError={true}
                disabled
                isDragging
              />
            ) : null}
          </DragOverlay>
          <Button
            sx={{ maxWidth: '200px' }}
            size="small"
            variant="outlined"
            onClick={onAddAlternative}
            disabled={textAlternatives.length >= 10}
          >
            {strings.polls.quizAddAlternative}
          </Button>
        </Stack>
      </Stack>
    </DndContext>
  );
};

export default PollTextAlternatives;
